<script setup>
import { onMounted, reactive, ref } from "vue";
import { useUserStore } from "../../../stores/user";
import { useRoute } from "vue-router/auto";
import { setPageTitle } from "../../../utils/pageTitle";
import OrderHeader from "../../../components/order/OrderHeader.vue";
import { shortDateWithTimeTz } from "@/utils/dateUtils";
import { useToast } from "vue-toastification";
import useOutboundOrders from "@/hooks/useOutboundOrders";
import OutboundOrderLayout from "@/layouts/OutboundOrderLayout.vue";

const {
  updatePage,
  outboundOrder,
  outboundLoadingCount,
  orderDocuments,
  fetchOutboundOrder,
  fetchOutboundOrderDocuments,
} = useOutboundOrders();
const userStore = useUserStore();
const route = useRoute();
const toast = useToast();
const localState = reactive({
  page: parseInt(route.query.page) || 1,
  searchText: "",
  filters: [],
  sort: "id",
});
const fileInput = ref(null);
const bol = ref(false);

onMounted(async () => {
  await fetchOutboundOrder(route.params.id).then(() => {
    setPageTitle(`Outbound Order #OUT-${outboundOrder.value.id}`);
  });
  fetchOutboundOrderDocuments(route.params.id, localState);
});

const uploadPhotos = async () => {
  if (fileInput.value.files.length == 0) {
    toast.error("Please select a file to upload.");
    return;
  }
  console.log("Uploading file:", fileInput.value.files[0], bol.value);
  const file = fileInput.value.files[0];
  const formData = new FormData();
  formData.append("file", file, file.name);
  // handle upload here
}

</script>

<template>
  <OutboundOrderLayout :outboundOrder="outboundOrder" v-if="outboundOrder">
    <div class="w-100">
      <div v-if="userStore.user && outboundOrder" class="px-4">
        <div class="w-100 px-2">
          <div class="w-100">
            <OrderHeader
              orderType="outbound"
              :orderId="outboundOrder.id"
              :status="outboundOrder.order_status.short_label"
            />
          </div>

          <v-card class="mt-4 pa-2 w-auto" elevation="0">
            <v-card-title class="text-h5">Order Documents</v-card-title>
            <v-card-text>
              <v-table>
                <thead>
                  <tr>
                    <th class="text-left text-uppercase">ID</th>
                    <th class="text-left text-uppercase">Type</th>
                    <th class="text-left text-uppercase">BOL</th>
                    <th class="text-left text-uppercase">Created By</th>
                    <th class="text-left text-uppercase">Created At</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    colspan="2"
                    class="text-center"
                    v-if="orderDocuments?.total_count < 1"
                  >
                    <td colspan="12">No documents for this order</td>
                  </tr>
                  <tr
                    v-if="!outboundLoadingCount && orderDocuments"
                    v-for="doc in orderDocuments.data"
                    :key="doc.id"
                  >
                    <td>
                      {{ doc.id }}
                    </td>
                    <td>{{ doc.mime_type }}</td>
                    <td>{{ doc.bol ? "Yes" : "No" }}</td>
                    <td>
                      <a :href="`/users/${doc.created_by_id}`">{{
                        doc.created_by?.name
                      }}</a>
                    </td>
                    <td>{{ shortDateWithTimeTz(doc.created_at) }}</td>
                  </tr>
                  <tr v-if="!orderDocuments">
                    <td colspan="12" class="text-center py-10">
                      <v-progress-circular
                        :size="40"
                        :width="4"
                        color="primary"
                        indeterminate
                      ></v-progress-circular>
                    </td>
                  </tr>
                </tbody>
              </v-table>
              <div class="mt-2" v-if="orderDocuments">
                <v-pagination
                  v-model="localState.page"
                  :length="orderDocuments.total_pages"
                  @update:modelValue="updatePage"
                  v-if="orderDocuments.total_pages > 0"
                ></v-pagination>
              </div>
            </v-card-text>

            <v-divider class="mt-4"></v-divider>
            <v-card-actions class="pa-2">
              <v-file-input label="File input" ref="fileInput" variant="outlined" density="compact" hide-details :max-width="500"></v-file-input>
              <v-checkbox
                v-model="bol"
                density="compact"
                label="Bill of Lading"
                hide-details
                class="ml-4"
              />
              <v-btn 
                variant="outlined"
                color="primary" 
                @click="uploadPhotos"
                density="compact"
                class="ml-4"
              >
                Upload
              </v-btn>
            </v-card-actions>
          </v-card>
        </div>
      </div>
    </div>
  </OutboundOrderLayout>
</template>
